import { Component, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AbstractComponent, Attendance, AttendanceService, ChannelTypeEnum, channelIconOf, unreadWeight } from 'lib-trend-core';
import { Observable, takeUntil } from 'rxjs';
import { adjustUnreadCount, getAttendances } from '../../state/actions';
import { AppState, PagerParamsState } from '../../state/app.state';
import { pagerParamsSelector, pinnedAttendancesSelector, whisperPendingSelector } from '../../state/selectors';

@Component({
    selector: 'attendance-contact-card-component',
    // templateUrl: 'attendance-contact-card.component.html',
    templateUrl: 'attendance-contact-card.v2.component.html',
    styleUrls: ['attendance-contact-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendanceContactCardComponent extends AbstractComponent implements OnChanges {

  public readonly pagerParams$: Observable<PagerParamsState> = this.store.select(pagerParamsSelector);
  public readonly pinnedAttendances$: Observable<Array<Attendance>> = this.store.select(pinnedAttendancesSelector);

  @Input() item: Attendance;
  @Input() selected: boolean = false;
  @Input() pinned: boolean = false;

  @Output() fixedStatusChanged = new EventEmitter<void>();

  public MAX_PINNED_ATTENDANCES: number = 3;

  pagerParamsState: PagerParamsState;
  pinnedAttendancesLength: number = 0;

  isThumbnailLoaded = false;
  unreadCount: number = 0;
  menuOpen = false;
  // A whisper addressed to the current user that they have not opened yet (RF-11).
  hasPendingWhisper = false;
  private whisperPendingIds: Array<string> = [];

  constructor(
    injector: Injector,
    private store: Store<AppState>,
    public attendanceService: AttendanceService
  ) {
    super(injector);
    this.pagerParams$.subscribe(pager => {
      this.pagerParamsState = pager;
    });
    this.pinnedAttendances$.subscribe(pinnedAttendances => {
      this.pinnedAttendancesLength = pinnedAttendances.length;
    });
    this.store.select(whisperPendingSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => {
        this.whisperPendingIds = ids ?? [];
        this.updatePendingWhisper();
      });
  }

  public get canPinnedAttendances(): boolean {
    return this.pinnedAttendancesLength < this.MAX_PINNED_ATTENDANCES;
  }

  onContactImageLoad(): void {
    this.isThumbnailLoaded = true;
  }

  onContactImageError(event: Event): void {
    this.isThumbnailLoaded = true;
    this.onImageError(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only the assignee clears the unread count (RF-27); a manager opening it leaves it as it is.
    if (changes['selected'] && changes['selected'].currentValue && (!this.item?.user || this.item.user._id === super.getIDCurrentUser())) {
      this.unreadCount = 0;
    }
    if (changes['item'] && changes['item'].currentValue) {
      this.unreadCount = this.item?.countUnreadMessages ?? 0;
      this.updatePendingWhisper();
    }
  }

  // The pending ids may arrive before this card receives its attendance, or after it.
  private updatePendingWhisper(): void {
    this.hasPendingWhisper = !!this.item?._id && this.whisperPendingIds.includes(this.item._id);
  }

  markAsUnread(): void {
    if (!this.item?._id) {
      return;
    }
    const previous = this.item.countUnreadMessages;
    this.attendanceService.markAsUnread(this.item._id).subscribe({
      next: (response) => {
        const current = response.data.countUnreadMessages;
        this.item = { ...this.item, countUnreadMessages: current };
        this.unreadCount = current;
        // The tab's badge rises at once for the marker; the server's event then confirms it
        // (SPEC-2026-10-attendance-tabs-unread-messages RF-13).
        const delta = unreadWeight(current) - unreadWeight(previous);
        if (delta !== 0) {
          this.store.dispatch(adjustUnreadCount({ status: this.item.status, delta }));
        }
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  public get channelIcon(): string {
    return channelIconOf(this.item.channel);
  }

  public get contentMessage(): string {
    if (this.item.lastMessage) {
      const text = this.item.lastMessage.content;
      return text.replace(/_\*.*?\*_/g, '').trim();
    }
    return '';
  }

  public pinnedAttendance(): void {
    this.attendanceService.pinnedAttendance(this.item._id, this.item.status).subscribe({
      next: () => this.store.dispatch(getAttendances({
        page: this.pagerParamsState.page,
        perPage: this.pagerParamsState.perPage,
        search: this.pagerParamsState.search,
        params: this.pagerParamsState.params,
      }))
    });
  }

  public unpinnedAttendance(): void {
    this.attendanceService.unpinnedAttendance(this.item._id, this.item.status).subscribe({
      next: () => this.store.dispatch(getAttendances({
        page: this.pagerParamsState.page,
        perPage: this.pagerParamsState.perPage,
        search: this.pagerParamsState.search,
        params: this.pagerParamsState.params,
      }))
    });
  }

}
